<template>
  <div class="completion-certificates">
    <grid
      ref="grid"
      :show-header="true"
      :list-url="listUrl"
      :add-checkmarks="true"
      loading-message="Loading completion certificates ..."
      :reset-filter="true"
      :show-total-records="showTotalRecords"
      @links-loaded="onGridLinksLoaded"
    >
      <template #header>
        <a
          v-if="showGenerateCertificates"
          href="#"
          class="btn btn-sm btn-primary me-2"
          title="Generate certificates for selected participants"
          @click.prevent="showGenerateCertificatesModal"
        >
          Generate certificates
        </a>
        <button
          v-if="showSendCertificates"
          type="button"
          class="btn btn-sm btn-primary me-2"
          title="Send certificates to selected participants"
          @click.prevent="showSendCertificatesModal"
        >
          E-mail certificates
        </button>
        <a
          v-if="showDownloadCertificates"
          href="#"
          class="btn btn-sm btn-primary me-2"
          title="Download certificates for selected participants"
          @click.prevent="showDownloadCertificatesModal"
        >
          Download certificates
        </a>
        <a
          v-if="showConfigureCertificates"
          href="#"
          class="btn btn-sm btn-primary ms-auto"
          title="Configure certificates"
          @click.prevent="showConfigureCertificatesModal"
        >
          Configure certificates
        </a>
      </template>
      <template #custom-cell="cellObj">
        <a
          v-if="canDownloadCertificate(cellObj.item)"
          :href="cellObj.item.links.download"
          title="Download certificate"
        >
          <span class="glyphicons download" />
        </a>
      </template>
    </grid>

    <teleport
      to="#vue-portal-target"
      append
    >
      <generate-completion-certificate-modal
        ref="generateModal"
        :participant-ids="generateModalProps.participantIds"
        :generate-url="generateModalProps.generateUrl"
        @certificates-generated="onCertificatesGenerated"
      />
      <modal
        ref="configureModal"
        modal-title="Configure certificates"
        :form-url="configureModalFormUrl"
        @success="onConfigurationSaved"
      />
      <modal
        ref="downloadModal"
        :modal-title="downloadModalProps.modalTitle"
        :form-url="downloadModalProps.formUrl"
        :form-params="downloadModalProps.formParams"
      />
      <modal
        ref="sendModal"
        :modal-title="sendModalProps.modalTitle"
        :form-url="sendModalProps.formUrl"
        :form-params="sendModalProps.formParams"
        @remote-form-loaded="onSendModalFormLoaded"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, useTemplateRef} from 'vue'
import $ from 'jquery'

import Grid from './grid.vue'
import Modal from './modal.vue'
import GenerateCompletionCertificateModal from './generate-completion-certificate-modal.vue'

import {getValueStrNullable, isNonEmptyString} from '../js-utils'
import type {GridID, GridItem} from './types'

interface CertificateLinks {
  configure?: string | null;
  download?: string | null;
  generate?: string | null;
  send?: string | null;
}

interface ModalFormParams {
  participant_ids: GridID[];
}

interface ModalProps {
  modalTitle: string;
  formUrl?: string;
  formParams: ModalFormParams;
}

type ModalInstance = InstanceType<typeof Modal> & {
  $el: Element;
}

defineProps({
  listUrl: {default: null, type: String},
  showTotalRecords: {default: false, type: Boolean},
})

const grid = useTemplateRef<InstanceType<typeof Grid>>('grid')
const generateModal = useTemplateRef<InstanceType<typeof GenerateCompletionCertificateModal>>('generateModal')
const configureModal = useTemplateRef<InstanceType<typeof Modal>>('configureModal')
const downloadModal = useTemplateRef<InstanceType<typeof Modal>>('downloadModal')
const sendModal = useTemplateRef<ModalInstance>('sendModal')

const configureUrl = ref<string | undefined>()
const sendUrl = ref<string | null>(null)
const downloadUrl = ref<string | null>(null)
const generateModalProps = ref({
  participantIds: [] as GridID[],
  generateUrl: undefined as string | undefined,
})
const configureModalFormUrl = ref<string | undefined>()
const downloadModalProps = ref<ModalProps>({
  modalTitle: 'Download certificates',
  formUrl: undefined,
  formParams: {
    participant_ids: []
  }
})
const sendModalProps = ref<ModalProps>({
  modalTitle: 'E-mail certificates',
  formUrl: undefined,
  formParams: {
    participant_ids: []
  }
})

const showGenerateCertificates = computed((): boolean => isNonEmptyString(generateModalProps.value.generateUrl))

const showConfigureCertificates = computed((): boolean => isNonEmptyString(configureUrl.value))

const showSendCertificates = computed((): boolean => isNonEmptyString(sendUrl.value))

const showDownloadCertificates = computed((): boolean => isNonEmptyString(downloadUrl.value))

const selectedParticipantIds = (): GridID[] => grid.value?.selectedItemIds || []

const showGenerateCertificatesModal = (): void => {
  const participantIds = selectedParticipantIds()
  if (participantIds.length == 0) {
    alert('Please select at least one participant')
    return
  }

  generateModalProps.value.participantIds = participantIds
  generateModal.value?.showModal()
}

const showConfigureCertificatesModal = (): void => {
  configureModal.value?.showModal()
}

const showSendCertificatesModal = (): void => {
  const participantIds = selectedParticipantIds()
  if (participantIds.length == 0) {
    alert('Please select at least one participant')
    return
  }

  sendModalProps.value.formParams.participant_ids = participantIds
  sendModal.value?.showModal()
}

const showDownloadCertificatesModal = (): void => {
  const participantIds = selectedParticipantIds()
  if (participantIds.length == 0) {
    alert('Please select at least one participant')
    return
  }

  downloadModalProps.value.formParams = {
    participant_ids: participantIds
  }

  nextTick(() => {
    downloadModal.value?.showModal()
  })
}

const onGridLinksLoaded = (links: CertificateLinks): void => {
  generateModalProps.value.generateUrl = getValueStrNullable(links.generate || null) || undefined
  configureUrl.value = getValueStrNullable(links.configure || null) || undefined
  configureModalFormUrl.value = configureUrl.value
  sendUrl.value = getValueStrNullable(links.send || null)
  sendModalProps.value.formUrl = sendUrl.value || undefined
  downloadUrl.value = getValueStrNullable(links.download || null)
  downloadModalProps.value.formUrl = downloadUrl.value || undefined
}

const onCertificatesGenerated = (): void => {
  grid.value?.reload()
}

const onConfigurationSaved = (): void => {
  configureModal.value?.hideModal()
}

const onSendModalFormLoaded = (): void => {
  if (!sendModal.value) {
    return
  }

  $(sendModal.value.$el).find('button[ref="sendCertificatesButton"]').on('click', (e) => {
    e.preventDefault()

    const url = sendUrl.value
    if (!isNonEmptyString(url)) {
      return
    }

    sendModal.value?.performRequest(url, {
      participant_ids: selectedParticipantIds(),
      send: 1
    }, null, (status, responseData) => {
      if (status) {
        sendModal.value?.setupForm(responseData)
      }
      grid.value?.reload()
    })
  })
}

const canDownloadCertificate = (item: GridItem): boolean => {
  return isNonEmptyString(item.links?.download)
}
</script>
